import { Fragment, ReactElement, ReactNode, cloneElement, isValidElement, useState } from 'react';

import {
  IEditorPolicy,
  IEditorPolicyInput,
  IEditorRuleInput,
  IRoutingConfigPoliciesTableColumns,
} from '@entities/routing/config-version';
import ModalConfirm from '@features/modal-confirm';
import { ITableActionsConfig, TableActionBtns } from '@features/table-action-btns';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Chip, IconButton, Stack, Switch, Typography } from '@mui/material';
import { useModal } from '@shared/components/Modal';

import ChangeMarker from '../components/ChangeMarker';
import { DraggableRow, DropZoneRow } from '../components/DraggableRow';
import PolicyConditions from '../components/PolicyConditions';
import PolicyEditorForm from '../components/PolicyEditorForm';
import RulesTable from '../components/RulesTable';

const POLICY_DRAG_TYPE = 'routing-policy-row';

// Rows are editor policies; TBody needs a string id, so the client uid is the row id.
export interface IRoutingConfigPolicyRow extends Omit<IEditorPolicy, 'id'> {
  id: string;
}

interface IOptions {
  // Unlocks the toggle, the row actions and drag-reorder.
  isEditing?: boolean;
  // Policies that differ from the saved version, marked on their rows.
  changedUids?: string[];
  // Subset of changedUids: policies created in this session.
  addedUids?: string[];
  // Same two lists for the rules inside the expanded policies.
  changedRuleUids?: string[];
  addedRuleUids?: string[];
  // Row order: drives the Priority chips and the drag positions.
  policyUids?: string[];
  onTogglePolicy?: (uid: string) => void;
  onUpdatePolicy?: (uid: string, input: IEditorPolicyInput) => void;
  onClonePolicy?: (uid: string) => void;
  onRemovePolicy?: (uid: string) => void;
  onMovePolicy?: (uid: string, toIndex: number) => void;
  onMoveRule?: (policyUid: string, ruleUid: string, toIndex: number) => void;
  onAddRule?: (policyUid: string, input: IEditorRuleInput) => void;
  onUpdateRule?: (policyUid: string, ruleUid: string, input: IEditorRuleInput) => void;
  onRemoveRule?: (policyUid: string, ruleUid: string) => void;
  onToggleRule?: (policyUid: string, ruleUid: string) => void;
}

type TRowElement = ReactElement<{ className?: string }>;

const formatRules = (count: number) => `${count} ${count === 1 ? 'rule' : 'rules'}`;

// Mirror of the Policies-page table; Actions and drag handles only in edit mode.
export const useTableSettings = ({
  isEditing = false,
  changedUids = [],
  addedUids = [],
  changedRuleUids = [],
  addedRuleUids = [],
  policyUids = [],
  onTogglePolicy,
  onUpdatePolicy,
  onClonePolicy,
  onRemovePolicy,
  onMovePolicy,
  onMoveRule,
  onAddRule,
  onUpdateRule,
  onRemoveRule,
  onToggleRule,
}: IOptions = {}) => {
  const { closeModal } = useModal();
  // Rows start collapsed, matching the Policies page.
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  const toggleRow = (id: string) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const columnTitles: IRoutingConfigPoliciesTableColumns = {
    expand: '',
    priority: 'Priority',
    name: 'Policy Name',
    status: 'On/Off',
    traffic: 'Traffic & Merchants Conditions',
    rules: 'Rules',
    ...(isEditing ? { actions: 'Actions' } : {}),
  };

  const renderMarkers = (policy: IRoutingConfigPolicyRow) => {
    const hasNoRules = isEditing && !policy.rules?.length;

    return (
      <>
        <ChangeMarker
          isAdded={addedUids.includes(policy.uid)}
          isChanged={changedUids.includes(policy.uid)}
        />
        {hasNoRules && (
          <span
            className='routing-policy-marker routing-policy-marker--invalid'
            title='A policy needs at least one rule — Save Draft is blocked until it has one'
          >
            no rules
          </span>
        )}
      </>
    );
  };

  const renderActions = (policy: IRoutingConfigPolicyRow) => {
    const ruleCount = policy.rules?.length ?? 0;
    const config: ITableActionsConfig = {
      clone: {
        isEnabled: true,
        isVisible: true,
        popupSize: 'sm',
        popupElem: (
          <ModalConfirm
            title='Clone policy'
            message={`Clone "${policy.name}"? The copy is created switched off.`}
            onConfirm={() => {
              onClonePolicy?.(policy.uid);
              closeModal();
            }}
            onCancel={closeModal}
          />
        ),
      },
      edit: {
        isEnabled: true,
        isVisible: true,
        popupSize: 'md',
        popupElem: (
          <PolicyEditorForm policy={policy} onSubmit={input => onUpdatePolicy?.(policy.uid, input)} />
        ),
      },
      delete: {
        isEnabled: true,
        isVisible: true,
        popupSize: 'sm',
        popupElem: (
          <ModalConfirm
            title='Delete policy'
            message={
              ruleCount
                ? `Do you want to delete policy "${policy.name}"? Its ${formatRules(ruleCount)} will be removed with it.`
                : `Do you want to delete policy "${policy.name}"?`
            }
            onConfirm={() => {
              onRemovePolicy?.(policy.uid);
              closeModal();
            }}
            onCancel={closeModal}
          />
        ),
      },
    };

    return <TableActionBtns config={config} />;
  };

  const renderItem = (
    key: keyof IRoutingConfigPoliciesTableColumns,
    policy: IRoutingConfigPolicyRow,
  ) => {
    switch (key) {
      case 'expand':
        return (
          <Stack direction='row' spacing={0.5} alignItems='center'>
            {isEditing && <DragIndicatorIcon fontSize='small' className='drag-handle' />}
            <IconButton size='small' onClick={() => toggleRow(policy.id)} aria-label='toggle'>
              {expandedRows[policy.id] ? <ExpandMoreIcon /> : <ChevronRightIcon />}
            </IconButton>
          </Stack>
        );
      case 'priority': {
        // Position is the priority Save Draft sends; the stored field is the fallback without row order.
        const position = policyUids.indexOf(policy.uid) + 1;
        return <Chip size='small' variant='filled' label={`${position || policy.priority}`} />;
      }
      case 'name':
        return (
          <Stack spacing={0.5}>
            <Stack direction='row' spacing={1} alignItems='center'>
              <Typography variant='body2' fontWeight={700}>
                {policy.name}
              </Typography>
              {renderMarkers(policy)}
            </Stack>
            {policy.description && (
              <Typography variant='caption' color='text.secondary'>
                {policy.description}
              </Typography>
            )}
          </Stack>
        );
      case 'status':
        return (
          <Switch
            checked={policy.active}
            disabled={!isEditing}
            onChange={() => onTogglePolicy?.(policy.uid)}
            inputProps={{ 'aria-label': `${policy.name} on/off` }}
          />
        );
      case 'traffic':
        return <PolicyConditions conditions={policy.conditions} />;
      case 'rules':
        return <Chip size='small' label={policy.rules?.length ?? 0} variant='filled' />;
      case 'actions':
        return isEditing ? renderActions(policy) : null;
      default:
        return null;
    }
  };

  const renderRow = (policy: IRoutingConfigPolicyRow, defaultRow: ReactNode) => {
    if (!isValidElement(defaultRow)) return defaultRow;
    const isExpanded = !!expandedRows[policy.id];
    const className = [
      'routing-policy-row',
      isExpanded ? 'expanded-row' : '',
      changedUids.includes(policy.uid) ? 'routing-policy-row--changed' : '',
    ]
      .filter(Boolean)
      .join(' ');
    const columnCount = Object.keys(columnTitles).length;
    const styledRow = cloneElement(defaultRow as TRowElement, { className });
    const rulesRow = isExpanded && (
      <tr className='routing-config-rules-row'>
        <td colSpan={columnCount}>
          <RulesTable
            rules={policy.rules ?? []}
            isEditing={isEditing}
            policyUid={policy.uid}
            changedRuleUids={changedRuleUids}
            addedRuleUids={addedRuleUids}
            onMoveRule={onMoveRule}
            onAddRule={onAddRule}
            onUpdateRule={onUpdateRule}
            onRemoveRule={onRemoveRule}
            onToggleRule={onToggleRule}
          />
        </td>
      </tr>
    );

    if (isEditing) {
      const index = policyUids.indexOf(policy.uid);

      return (
        <DraggableRow
          key={policy.id}
          type={POLICY_DRAG_TYPE}
          id={policy.uid}
          index={index}
          row={styledRow}
          onMove={onMovePolicy}
        >
          {rulesRow}
          {index === policyUids.length - 1 && (
            <DropZoneRow
              type={POLICY_DRAG_TYPE}
              count={policyUids.length}
              row={
                <tr>
                  <td colSpan={columnCount} />
                </tr>
              }
              onMove={onMovePolicy}
            />
          )}
        </DraggableRow>
      );
    }

    return (
      <Fragment key={policy.id}>
        {styledRow}
        {rulesRow}
      </Fragment>
    );
  };

  return { columnTitles, renderItem, renderRow };
};
